import { debounce } from 'application/utils/helpers'

/**
 * Класс для управления полем ввода поиска.
 * Обрабатывает ввод текста, очистку поля и вызывает callback при изменении значения.
 */
export class SearchInput {
  private input: HTMLInputElement
  private clearBtn: HTMLElement
  private onChange: ((_value: string) => void) | null = null

  /**
   * Создает экземпляр SearchInput.
   * @param {HTMLElement} container - HTML элемент-контейнер поля поиска
   * @param {Function} onChange - Callback-функция, вызываемая при изменении значения поиска
   */
  constructor(container: HTMLElement, onChange: (_value: string) => void) {
    this.onChange = onChange
    this.input = container.querySelector('input') as HTMLInputElement
    this.clearBtn = container.querySelector(
      '[data-action="clear"]'
    ) as HTMLElement

    this.init()
  }

  /**
   * Инициализирует поле поиска и устанавливает обработчики событий.
   * @private
   */
  private init() {
    this.updateClearButtonVisibility()
    this.input.addEventListener('input', this.updateClearButtonVisibility)
    this.input.addEventListener('input', debounce(this.onInput, 1000))
    this.input.addEventListener('keydown', this.onEnter)
    this.clearBtn.addEventListener('click', this.onClear)
  }

  /**
   * Обрабатывает событие ввода текста в поле поиска.
   * Вызывает onChange callback с debounce задержкой.
   * @private
   */
  private onInput = () => {
    const value = this.input.value.trim()
    this.onChange?.(value)
  }

  /**
   * Обрабатывает нажатие клавиши Enter в поле поиска.
   * @param {KeyboardEvent} event - Событие клавиатуры
   * @private
   */
  private onEnter = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      const value = this.input.value.trim()
      this.onChange?.(value)
    }
  }

  /**
   * Обрабатывает клик по кнопке очистки поля поиска.
   * Очищает поле и вызывает onChange callback с пустой строкой.
   * @private
   */
  private onClear = () => {
    this.input.value = ''
    this.updateClearButtonVisibility()
    this.onChange?.('')
  }

  /**
   * Обновляет видимость кнопки очистки в зависимости от наличия текста в поле.
   * @private
   */
  private updateClearButtonVisibility = () => {
    this.clearBtn.classList.toggle('is-shown', this.input.value.length > 0)
  }
}
